// client/src/components/labEngine/NotebookCell.jsx

import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import Editor from '@monaco-editor/react';
import { usePyodide } from './PyodideContext';
import { useNodeJS }  from './NodeJSContext';
import { Play, Trash2, Plus, GripVertical, CirclePlus, CircleMinus } from 'lucide-react';
import CustomAlert from '../customs/customAlert';
import { validatePinInputs } from '../../utilities/validations/notebookCodeValidation';
import showNotification from '../../utilities/showNotification';
import { CourseDomains } from '../../types/courseDomains';

import './notebookCell.css';

// Key part: letters/digits/spaces/underscore/slash/hyphen/parens, up to 60
// chars — wide enough for keys like "LCEL Chain response (invoke_singleinput)".
// const STDOUT_KV_RE           = /^([A-Za-z][A-Za-z0-9 _/()-]{1,60}):\s?(.*)$/;
const STDOUT_KV_RE           = /^([^:\n]{1,80}):\s?(.*)$/;
const STDOUT_NUMBER_RE       = /^(\d+)\.\s+(.+)$/;
// "  [0] system: text" — indented bracketed-index sub-item (enumerate() output)
const STDOUT_BRACKET_RE      = /^(\s*)\[(\d+)\]\s*([A-Za-z][A-Za-z0-9 _]{0,30}):\s*(.*)$/;
// "      human:  text" — indented plain key:value continuation line
const STDOUT_INDENTED_KV_RE  = /^(\s{2,})([A-Za-z][A-Za-z0-9 _]{0,30}):\s*(.*)$/;
// A value or whole line that is itself a JSON object/array
const JSON_SHAPE_RE          = /^[{[][\s\S]*[}\]]$/;
const STDOUT_HEADER_KEYS = new Set(['answer', 'question', 'chain', 'retrieval mode']);

// Best-effort pretty-print: if it parses as JSON, re-stringify with indent;
// otherwise return the original text untouched (never throw on malformed/
// truncated JSON-looking fragments — e.g. output cut off by a slice()).
function tryPrettyJson(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

// Converts literal "\n" sequences (as they appear inside JSON string values)
// into real line breaks, and **bold** markers into styled <strong> spans.
const HEADING_RE = /^(#{1,6})\s+(.*)$/;

// Fixes bold markers the model split across a line break, e.g.
// "**Category:\n ** Billing" → "**Category:** Billing"
function normalizeBrokenBold(text) {
  return text.replace(/\*\*([^*\n]+):\s*\n\s*\*\*/g, '**$1:** ');
}

function formatInlineText(text) {
  if (typeof text !== 'string') return text;
  const normalized = normalizeBrokenBold(text.replace(/\\n/g, '\n'));
  const lines = normalized.split('\n');
  return lines.map((line, i) => {
    const headingMatch = line.match(HEADING_RE);
    if (headingMatch) {
      const [, hashes, content] = headingMatch;
      const level = hashes.length;
      return (
        <div key={i} className={`nb-output__heading nb-output__heading--h${level}`}>
          {renderBoldSegments(content)}
        </div>
      );
    }
    return (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {renderBoldSegments(line)}
      </React.Fragment>
    );
  });
}
function renderBoldSegments(line) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="nb-output__bold">{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// Renders a JSON blob with each line's "key": portion styled distinctly
// from its value — i.e. everything from the start of the line up through
// the colon gets its own span, per your ask.
function JsonBlock({ text }) {
  const pretty = tryPrettyJson(text);
  const lines = pretty.split('\n');
  return (
    <div className="nb-output__json">
      {lines.map((line, i) => {
        const m = line.match(/^(\s*)"([^"]+)":\s?(.*)$/);
        if (m) {
          const [, indent, key, rest] = m;
          return (
            <div key={i} className="nb-output__json-line">
              {indent}
              <span className="nb-output__json-key">&quot;{key}&quot;:</span>
              {rest && <span className="nb-output__json-value"> {formatInlineText(rest)}</span>}
            </div>
          );
        }
        return <div key={i} className="nb-output__json-line">{line}</div>;
      })}
    </div>
  );
}

function renderStdoutLine(line, i) {
  if (!line.trim()) {
    return <div key={i} className="nb-output__stdout-spacer" />;
  }

  const bracketMatch = line.match(STDOUT_BRACKET_RE);
  if (bracketMatch) {
    const [, , idx, key, value] = bracketMatch;
    return (
      <div key={i} className="nb-output__subitem">
        <span className="nb-output__subitem-idx">[{idx}]</span>
        <span className="nb-output__subitem-key">{key}:</span>
        <span className="nb-output__subitem-value">{formatInlineText(value.trim())}</span>
      </div>
    );
  }

  const sourceHeaderMatch = line.match(STDOUT_SOURCE_HEADER_RE);
  if (sourceHeaderMatch) {
    const [, idx, source] = sourceHeaderMatch;
    const isTruncated = !source || source.trim() === '';
    return (
      <div key={i} className={`nb-output__source-header ${isTruncated ? 'nb-output__source-header--truncated' : ''}`}>
        <span className="nb-output__chunk-idx">[{idx}]</span>
        <span className="nb-output__source-label">
          {isTruncated ? '⚠ source cut off' : `Source: ${source.trim()}`}
        </span>
      </div>
    );
  }

  // ── NEW: scored/ranked chunk lines ──────────────────────────────────
  const chunkMatch = line.match(STDOUT_CHUNK_RE);
  if (chunkMatch) {
    const [, , idx, score, text] = chunkMatch;
    return (
      <div key={i} className="nb-output__chunk">
        <span className="nb-output__chunk-idx">[{idx}]</span>
        {score && <span className="nb-output__chunk-score">score {score}</span>}
        <span className="nb-output__chunk-text">{formatInlineText(text.trim())}</span>
      </div>
    );
  }


  const indentedKvMatch = line.match(STDOUT_INDENTED_KV_RE);
  if (indentedKvMatch) {
    const [, , key, value] = indentedKvMatch;
    return (
      <div key={i} className="nb-output__subitem nb-output__subitem--plain">
        <span className="nb-output__subitem-key">{key}:</span>
        <span className="nb-output__subitem-value">{formatInlineText(value.trim())}</span>
      </div>
    );
  }

  const kvMatch = line.match(STDOUT_KV_RE);
  if (kvMatch) {
    const [, key, value] = kvMatch;
    const keyTrim = key.trim();
    const valueTrim = value.trim();               // ← added
    const isHeader = STDOUT_HEADER_KEYS.has(keyTrim.toLowerCase());
    const isResponse = /response/i.test(keyTrim);
    const trimmedValue = valueTrim;                // ← use trimmed value from here on
    const valueIsJson = trimmedValue !== '' && JSON_SHAPE_RE.test(trimmedValue);
    const kvClass = [
      'nb-output__kv',
      isHeader && 'nb-output__kv--header',
      isResponse && 'nb-output__kv--response',
    ].filter(Boolean).join(' ');
    return (
      <div key={i} className={kvClass}>
        <span className={`nb-output__kv-key${isResponse ? ' nb-output__kv-key--response' : ''}`}>{key}:</span>
        {valueIsJson
          ? <JsonBlock text={trimmedValue} />
          : (trimmedValue && <span className="nb-output__kv-value"> {formatInlineText(trimmedValue)}</span>)}
      </div>
    );
  }

  const numMatch = line.match(STDOUT_NUMBER_RE);
  if (numMatch) {
    const [, num, text] = numMatch;
    return (
      <div key={i} className="nb-output__list-item">
        <span className="nb-output__list-num">{num}.</span>
        <span className="nb-output__list-text">{text}</span>
      </div>
    );
  }

  // A bare line that is itself a JSON object/array (no "Key:" prefix at all)
  if (JSON_SHAPE_RE.test(line.trim())) {
    return <JsonBlock key={i} text={line.trim()} />;
  }

  return <div key={i} className="nb-output__stdout-line">{formatInlineText(line)}</div>;
}

// ── Graph Runner output — detected & parsed into a styled timeline ────────
const GR_START_RE      = /^Graph Runner — starting execution/;
const GR_INPUT_RE      = /Input:\s*\n"([\s\S]*?)"\s*\n/;
const GR_STEP_RE       = /Step (\d+):\s*\n\s*\[(\w+)\] wrote state\.(\w+) → ([\s\S]*?)(?=\n(?:\[[\w]+\]|Step \d+:|⚠️|Run complete —)|$)/g;
const GR_WARNING_RE    = /^⚠️\s+(.*)$/gm;
const GR_COMPLETE_RE   = /Run complete — graph reached END after (\d+) supersteps\./;
const GR_SUMMARY_RE    = /Graph Runner complete — steps: (\d+)/;
const GR_TRACE_HEAD_RE = /Execution trace:\s*\n([\s\S]*?)(?=\nGraph Runner complete —|$)/;
const GR_STATUS_RE     = /\n?STATUS:\s*(\S+)\s*$/;
const STDOUT_CHUNK_RE = /^(\s*)\[(\d+)\]\s*(?:score=([\d.]+)\s*\|\s*)?(.*)$/;
const STDOUT_SOURCE_HEADER_RE = /^\s*\[(\d+)\]\s*\(Source:\s*([^)]*)\)?\s*$/;

function isGraphRunnerOutput(stdout) {
  return typeof stdout === 'string' && GR_START_RE.test(stdout.trim());
}

const NODE_COLORS = [
  { bg: '#E6F1FB', color: '#185FA5' },
  { bg: '#EEEDFE', color: '#534AB7' },
  { bg: '#E1F5EE', color: '#0F6E56' },
  { bg: '#FAECE7', color: '#993C1D' },
  { bg: '#FAEEDA', color: '#854F0B' },
];
function nodeColor(nodeName) {
  const sum = [...nodeName].reduce((a, c) => a + c.charCodeAt(0), 0);
  return NODE_COLORS[sum % NODE_COLORS.length];
}

function statusTone(value) {
  const v = value.toLowerCase();
  if (/escalat|fail|error|retr/i.test(v)) return 'warn';
  if (/resolved|success|pass|complete|ok/i.test(v)) return 'ok';
  return 'neutral';
}

function extractStatus(text) {
  const m = text.match(GR_STATUS_RE);
  if (!m) return { body: text, status: null };
  return { body: text.replace(GR_STATUS_RE, ''), status: m[1] };
}

function parseGraphRunnerOutput(stdout) {
  const inputMatch = stdout.match(GR_INPUT_RE);
  const completeMatch = stdout.match(GR_COMPLETE_RE);
  const summaryMatch = stdout.match(GR_SUMMARY_RE);

  // Steps + warnings, interleaved by their position in the string
  const items = [];
  let m;
  GR_STEP_RE.lastIndex = 0;
  while ((m = GR_STEP_RE.exec(stdout)) !== null) {
    items.push({ type: 'step', index: m.index, step: m[1], node: m[2], field: m[3], text: m[4].trim() });
  }
  GR_WARNING_RE.lastIndex = 0;
  while ((m = GR_WARNING_RE.exec(stdout)) !== null) {
    items.push({ type: 'warning', index: m.index, text: m[1].trim() });
  }
  items.sort((a, b) => a.index - b.index);

  // Execution trace — per-node segments
  const traceMatch = stdout.match(GR_TRACE_HEAD_RE);
  const traceEntries = [];
  if (traceMatch) {
    const traceBody = traceMatch[1];
    const segments = traceBody.split(/\n(?=\[\w+\] input:)/).filter(Boolean);
    segments.forEach(seg => {
      const headMatch = seg.match(/^\[(\w+)\] input:\s*\n([\s\S]*?)\n(?=\s*🔧|\s*\[\w+\] wrote)/);
      if (!headMatch) return;
      const node = headMatch[1];
      const input = headMatch[2].trim();

      const tools = [];
      const toolRe = /🔧 (\w+)\(([\s\S]*?)\) → ([\s\S]*?)(?=\n\s*🔧|\n\s*\[\w+\] wrote|$)/g;
      let tm;
      while ((tm = toolRe.exec(seg)) !== null) {
        tools.push({ name: tm[1], args: tm[2].trim(), result: tm[3].trim() });
      }

      const wroteMatch = seg.match(/\[\w+\] wrote state\.(\w+) → ([\s\S]*?)$/);
      traceEntries.push({
        node, input, tools,
        field: wroteMatch?.[1] || null,
        wrote: wroteMatch?.[2]?.trim() || null,
      });
    });
  }

  return {
    input: inputMatch?.[1] || null,
    items,
    completeSteps: completeMatch?.[1] || null,
    summarySteps: summaryMatch?.[1] || null,
    traceEntries,
  };
}

const GraphRunnerOutput = ({ stdout }) => {
  const [showTrace, setShowTrace] = useState(false);
  const parsed = parseGraphRunnerOutput(stdout);

  return (
    <div className="gr-output">
      <div className="gr-banner gr-banner--start">🚀 Graph Runner — starting execution</div>

      {parsed.input && (
        <div className="gr-input-card">
          <span className="gr-input-icon">💬</span>
          <span className="gr-input-text">{parsed.input}</span>
        </div>
      )}

      <div className="gr-timeline">
        {parsed.items.map((item, i) => {
          if (item.type === 'warning') {
            return (
              <div key={i} className="gr-warning">⚠️ {item.text}</div>
            );
          }
          const { body, status } = extractStatus(item.text);
          const colors = nodeColor(item.node);
          return (
            <div key={i} className="gr-step">
              <div className="gr-step__marker">{item.step}</div>
              <div className="gr-step__card">
                <div className="gr-step__head">
                  <span className="gr-node-pill" style={{ background: colors.bg, color: colors.color }}>
                    {item.node}
                  </span>
                  <span className="gr-step__field">wrote state.{item.field}</span>
                  {status && (
                    <span className={`gr-status-pill gr-status-pill--${statusTone(status)}`}>
                      {status}
                    </span>
                  )}
                </div>
                <div className="gr-step__body">{formatInlineText(body.trim())}</div>
              </div>
            </div>
          );
        })}
      </div>

      {parsed.completeSteps && (
        <div className="gr-banner gr-banner--complete">
          ✅ Run complete — graph reached END after {parsed.completeSteps} supersteps
        </div>
      )}

      {parsed.traceEntries.length > 0 && (
        <div className="gr-trace">
          <button className="gr-trace__toggle" onClick={() => setShowTrace(p => !p)}>
            {showTrace ? '▾' : '▸'} Execution trace ({parsed.traceEntries.length} node calls)
          </button>
          {showTrace && (
            <div className="gr-trace__body">
              {parsed.traceEntries.map((entry, i) => {
                const colors = nodeColor(entry.node);
                return (
                  <div key={i} className="gr-trace-entry">
                    <span className="gr-node-pill" style={{ background: colors.bg, color: colors.color }}>
                      {entry.node}
                    </span>
                    <div className="gr-trace-entry__input">{entry.input}</div>
                    {entry.tools.map((t, ti) => (
                      <div key={ti} className="gr-tool-chip">
                        <span className="gr-tool-chip__icon">🔧</span>
                        <code className="gr-tool-chip__call">{t.name}({t.args})</code>
                        <div className="gr-tool-chip__result">{t.result}</div>
                      </div>
                    ))}
                    {entry.wrote && (
                      <div className="gr-trace-entry__wrote">
                        <span className="gr-step__field">wrote state.{entry.field}</span>
                        {formatInlineText(extractStatus(entry.wrote).body.trim())}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {parsed.summarySteps && (
        <div className="gr-banner gr-banner--summary">
          🏁 Graph Runner complete — {parsed.summarySteps} steps
        </div>
      )}
    </div>
  );
};

const NotebookCell = forwardRef(({
  id,
  index,
  simulatorId = null,
  category = '',
  initialCode = '',
  pythonCode = '',
  javascriptCode = '',
  domain = '',
  executionContext = 'pyodide',
  simOutputs = [],
  tag = null,
  label = '',
  componentId = '',
  savedCode = '',
  onSaveCell,
  onRegenerateCell,
  isRegenerating = false,
  onDelete,
  onInsertBelow,
  onCodeChange,
  onMove,
  isLast,
  onCellOutput,
  onEditorMount,
  onTranslateCell,
  onJsCodeChange,
  generationStatus = 'done',        
  generationError  = '',            
  onRetryGenerateCell,
}, ref) => {
  const pyodideCtx = usePyodide();
  const nodejsCtx  = useNodeJS();

  const activeCtx = executionContext === 'nodejs' ? nodejsCtx : pyodideCtx;
  const runCode   = activeCtx?.runCode;
  const pyLoading = activeCtx?.isLoading ?? false;
  const pyodide   = executionContext === 'nodejs' ? null : pyodideCtx?.pyodide;

  // const pyodide   = pyodideCtx?.pyodide;
  // const { runCode, isLoading: pyLoading } = { runCode: pyodideCtx.runCode, isLoading: pyodideCtx.isLoading };
  
  const [output,    setOutput]    = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runCount,  setRunCount]  = useState(null);
  const [isActive,  setIsActive]  = useState(false);
  const [testFiles, setTestFiles]             = useState([]);
  const [showTestMenu, setShowTestMenu]       = useState(false);
  const [selectedTestFile, setSelectedTestFile] = useState(null);
  const [alertVisible, setAlertVisible]       = useState(false);
  const [alertConfig,  setAlertConfig]        = useState({});
  // Language toggle — AgenticAI only
  const [displayLang, setDisplayLang] = useState('python');
  const [jsCode, setJsCode] = useState(javascriptCode);
  const [isTranslating, setIsTranslating] = useState(false);
  // `useState(javascriptCode)` above only seeds jsCode at mount — once this
  // cell exists, regenerating its Python (e.g. after fixing an upstream
  // wire) pushes a new `javascriptCode` prop down (cleared to '' by the
  // parent) that this already-mounted instance would otherwise never see,
  // leaving stale/incorrect JS sitting in jsCode indefinitely. Resync on
  // every genuine prop change so "Regenerate" behaves like a fresh mount.
  useEffect(() => {
    setJsCode(javascriptCode);
  }, [javascriptCode]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const FILE_UPLOAD_COMPONENT_IDS = CourseDomains[domain]?.fileUploadComponentIds || [];
  const IMAGE_COMPONENT_IDS       = CourseDomains[domain]?.imageComponentIds || [];
  const categoryTags = CourseDomains[domain]?.cellCategoryTags || {};
  const resolvedTag  = categoryTags[category] || tag || null;

  const lastTranslatedPythonRef = useRef(initialCode);
  const displayLangRef = useRef('python');
  const codeRef   = useRef(initialCode);
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);

  // Own, deterministic layout — Monaco's built-in `automaticLayout` polls/
  // observes the container independently of our code, and with ~20 live
  // editor instances stacked on one page, a layout race during that polling
  // can make it measure a transient/wrong (sometimes near-zero) size; once
  // that happens nothing else ever corrects it (our onDidChangeModelContent
  // hook only fires on actual text edits, not container resizes), so the
  // wrong height sticks indefinitely — observed happening purely with idle
  // time, with no click/focus change needed. automaticLayout is disabled
  // below and this function is now the ONLY thing that ever calls
  // editor.layout(), driven by real triggers: mount, content change,
  // visibility/focus regain, and window resize.
  const relayoutRef = useRef(() => {});
  relayoutRef.current = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const lineCount = editor.getModel()?.getLineCount() || 1;
    const height = Math.max(lineCount * 20 + 20, 60);
    const width = editorContainerRef.current?.clientWidth || editor.getLayoutInfo().width;
    editor.layout({ height, width });
  };

  useEffect(() => {
    const relayout = () => relayoutRef.current();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') relayout();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', relayout);
    window.addEventListener('resize', relayout);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', relayout);
      window.removeEventListener('resize', relayout);
    };
  }, []);

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    onEditorMount?.(editor);
    const updateHeight = () => relayoutRef.current();
    editor.onDidChangeModelContent(updateHeight);
    updateHeight();
  };

  const pushCodeToEditor = (newCode) => {
    codeRef.current = newCode;
    onCodeChange(id, newCode);
    if (editorRef.current) {
      const model = editorRef.current.getModel();
      if (model) model.setValue(newCode);
    }
  };

  // Same reasoning as the javascriptCode resync above, for the Python side:
  // codeRef/Monaco's defaultValue only capture initialCode once, at first
  // mount. Regenerating this cell's Python (e.g. after a config change)
  // pushes a new `initialCode` prop down that an already-mounted instance
  // would otherwise never see, leaving codeRef.current — and therefore
  // whatever "Translate" sends to the server — frozen at a stale snapshot
  // even though the regenerated Python looks correct everywhere else.
  // Only the ref needs updating here — the live Monaco model is already
  // kept in sync via the parent's separate cellEditorRefs path (see
  // updateCellCode in notebookSimulator.jsx), so calling pushCodeToEditor
  // (which also calls editor.getModel().setValue()) would redundantly
  // re-set the same model a second time, triggering an extra
  // onDidChangeModelContent -> updateHeight() cycle that visibly shrank
  // the editor box.
  useEffect(() => {
    if (initialCode !== codeRef.current) {
      codeRef.current = initialCode;
    }
  }, [initialCode, label, id]);

  const executeRun = async (currentCode, overrideJs) => {
    // AgenticAI: always execute JS version regardless of display language
    const codeToRun = executionContext === 'nodejs' ? (overrideJs ?? jsCode ?? currentCode) : currentCode;
    console.log('🐛 [executeRun] executionContext:', executionContext);
    console.log('🐛 [executeRun] jsCode is empty?', !jsCode, '| jsCode length:', jsCode?.length);
    console.log('🐛 [executeRun] currentCode length:', currentCode?.length);
    console.log('🐛 [executeRun] codeToRun first 200 chars:', codeToRun?.slice(0, 200));
    
    const allCodes = window.__notebookCellCodes || [];

    const currentIndex = allCodes.findIndex(c => c.id === id);
    if (currentIndex > 0) {
      const previousCodes = allCodes.slice(0, currentIndex).map(c => c.code);
      const validationErrors = validatePinInputs(currentCode, previousCodes, currentIndex);
      if (validationErrors.length > 0) {
        const errOutput = { stdout: '', plot: null, error: validationErrors.join('\n') };
        setOutput(errOutput);
        return { ok: false, reason: 'validation', result: errOutput };
      }
    }
    setIsRunning(true);
    setOutput(null);
    try {
      const result = await runCode(codeToRun, id, simulatorId);
      setOutput(result);
      setRunCount(prev => (prev ?? 0) + 1);
      onCellOutput?.(id, componentId, result);
      // Persist the freshly-translated JS (if this was a nodejs run) so the
      // next session doesn't have to pay for another translation call.
      onSaveCell?.(id, currentCode, result, executionContext === 'nodejs' ? codeToRun : undefined);
      return { ok: !result?.error, result };
    } finally {
      setIsRunning(false);
    }
  };

  // Returns { ok, reason? } — used both by the Run button and by the
  // notebook-level "Run All" action, which needs to know whether to stop.
  const handleRun = useCallback(async () => {
    if (isRunning || pyLoading || isTranslating) return { ok: false, reason: 'busy' };

    if (executionContext === 'nodejs') {
      // Run never generates or translates — it only executes whatever
      // JavaScript already exists. If there's none, or the Python has
      // changed since the last Translate, tell the user to translate first
      // instead of silently paying for another AI call.
      if (!jsCode) {
        showNotification('No JavaScript version yet — click Translate first.', 'error');
        return { ok: false, reason: 'no-js' };
      }
      const pythonUnchanged = codeRef.current === lastTranslatedPythonRef.current;
      if (!pythonUnchanged) {
        showNotification('Python has changed since the last translation — click Translate before running.', 'error');
        return { ok: false, reason: 'stale-translation' };
      }
      return executeRun(codeRef.current, jsCode);
    }

    return executeRun(codeRef.current);
  }, [isRunning, pyLoading, isTranslating, jsCode, executionContext]);

  useImperativeHandle(ref, () => ({
    run: handleRun,
    clearOutput: () => setOutput(null),
  }), [handleRun]);

  const handleSave = () => {
    onSaveCell?.(id, codeRef.current);
  };

  const handleTranslate = async () => {
    if (isTranslating) return;
    setIsTranslating(true);
    try {
      // const response = await onTranslateCell?.(id, codeRef.current);
      const response = await onTranslateCell?.(id, codeRef.current, componentId);
      if (response?.success) {
        setJsCode(response.data.javascriptCode);
        onJsCodeChange?.(id, response.data.javascriptCode);
        lastTranslatedPythonRef.current = codeRef.current;
        // Persist immediately — otherwise a manual Translate that's never
        // followed by Run is lost the moment the user navigates away, and
        // gets silently re-translated (another gpt-4o call) next session.
        onSaveCell?.(id, codeRef.current, null, response.data.javascriptCode);
        showNotification('JavaScript version updated!', 'success');
      } else {
        showNotification(response?.error || 'Translation failed', 'error');
      }
    } catch (err) {
      showNotification('Translation failed', 'error');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;
    const uploadedName = file.name;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const content = new Uint8Array(evt.target.result);
      pyodide.FS.writeFile(`/home/pyodide/${uploadedName}`, content);
      const updatedCode = codeRef.current.replace(
        /FILE_PATH\s*=\s*['"][^'"]*['"]/,
        `FILE_PATH = '/home/pyodide/${uploadedName}'`
      );
      pushCodeToEditor(updatedCode);
      onSaveCell?.(id, updatedCode);
      setSelectedTestFile(uploadedName);
    };

    if (configuredPath && !configuredPath.endsWith(uploadedName)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Configured path: ${configuredPath}\nUploaded file: ${uploadedName}\n\nUpdate FILE_PATH to use "${uploadedName}"?`,
        onConfirm: () => {
          setAlertVisible(false);
          reader.readAsArrayBuffer(file);
        }
      });
      setAlertVisible(true);
      return;
    }

    reader.readAsArrayBuffer(file);
  };

  const handleTestFileSelect = async (filename) => {
    setShowTestMenu(false);

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;

    if (configuredPath && !configuredPath.endsWith(filename)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Current path: ${configuredPath}\nSelected file: ${filename}\n\nUpdate FILE_PATH to use "${filename}"?`,
        onConfirm: async () => {
          setAlertVisible(false);
          const res = await fetch(`/aitestdata/${filename}`);
          const text = await res.text();
          const encoder = new TextEncoder();
          pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
          const updatedCode = codeRef.current.replace(
            /FILE_PATH\s*=\s*['"][^'"]*['"]/,
            `FILE_PATH = '/home/pyodide/${filename}'`
          );
          pushCodeToEditor(updatedCode);
          onSaveCell?.(id, updatedCode);
          setSelectedTestFile(filename);
        }
      });
      setAlertVisible(true);
      return;
    }

    const res = await fetch(`/aitestdata/${filename}`);
    const text = await res.text();
    const encoder = new TextEncoder();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
    const updatedCode = codeRef.current.replace(
      /FILE_PATH\s*=\s*['"][^'"]*['"]/,
      `FILE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const confirmAction = (message, action, opts = {}) => {
    setAlertConfig({
      title: opts.title || 'Are you sure?',
      icon: opts.icon || '❓',
      message,
      onConfirm: () => {
        setAlertVisible(false);
        action();
      },
      onCancel: () => setAlertVisible(false),
    });
    setAlertVisible(true);
  };

  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      confirmAction('Run this cell?', handleRun, { title: 'Run Cell', icon: '▶️' })
      // handleRun();
    }
  };

  const handleTestDataClick = async () => {
    if (!showTestMenu) {
      try {
        const indexFile = IMAGE_COMPONENT_IDS.includes(componentId)
          ? '/aitestdata/images-index.json'
          : '/aitestdata/index.json';
        const res = await fetch(indexFile);
        const files = await res.json();
        setTestFiles(files);
      } catch {
      setTestFiles(IMAGE_COMPONENT_IDS.includes(componentId)
        ? ['cat.jpg', 'dog.jpg']
        : ['iris_test.csv', 'housing.csv', 'titanic.csv']);
      }
    }
    setShowTestMenu(prev => !prev);
  };

  const handleTestImageSelect = async (filename) => {
    setShowTestMenu(false);
    const res = await fetch(`/aitestdata/images/${filename}`);
    const buf = await res.arrayBuffer();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, new Uint8Array(buf));
    const updatedCode = codeRef.current.replace(
      /IMAGE_PATH\s*=\s*['"][^'"]*['"]/,
      `IMAGE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const hasOutput = output && (output.stdout || output.plot || output.error);

  // ── Cell-by-cell generation states — rendered instead of the full editor
  // until this cell's Python has actually been generated (or fails).
  if (generationStatus === 'pending') {
    return (
      <div className="nb-cell nb-cell--pending">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[ ]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          ⏳ Waiting to generate…
        </div>
      </div>
    );
  }

  if (generationStatus === 'generating') {
    return (
      <div className="nb-cell nb-cell--generating">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[*]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          <div className="nb-running__dot" />
          <div className="nb-running__dot" />
          <div className="nb-running__dot" />
          <span className="nb-running__label">🤖 Generating…</span>
        </div>
      </div>
    );
  }

  if (generationStatus === 'error') {
    return (
      <div className="nb-cell nb-cell--generation-error">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[!]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          <pre className="nb-output__error">❌ {generationError || 'Generation failed'}</pre>
          <button
            onClick={() => onRetryGenerateCell?.(id)}
            className="nb-btn--run"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      className={`nb-cell ${isActive ? 'nb-cell--active' : ''}`}
    >
      {/* ── Cell toolbar ─────────────────────────────────────────────────── */}
      <div className="nb-toolbar">
        <GripVertical size={14} className="nb-toolbar__grip" />
      
        <button
          onClick={() => setIsCollapsed(prev => !prev)}
          title={isCollapsed ? 'Expand cell' : 'Collapse cell'}
          className="nb-btn--icon nb-btn--collapse"
        >
          {isCollapsed ? <CirclePlus size={15} /> : <CircleMinus size={15} />}
        </button>

        {/* Move up/down */}
        {index > 0 && (
          <button
            onClick={() => onMove(id, 'up')}
            title="Move cell up"
            className="nb-btn--icon nb-btn--move"
          >
            ▲
          </button>
        )}
        {!isLast && (
          <button
            onClick={() => onMove(id, 'down')}
            title="Move cell down"
            className="nb-btn--icon nb-btn--move"
          >
            ▼
          </button>
        )}

        <span className="nb-toolbar__run-count">
          {isRunning ? '[*]' : runCount !== null ? `[${runCount}]` : '[ ]'}
        </span>

        {resolvedTag && (
          <span
            className="nb-badge"
            style={{ background: resolvedTag.bg, color: resolvedTag.color }}
          >
            {resolvedTag.label}
          </span>
        )}

        <span className="nb-label">
          {label || (executionContext === 'nodejs' ? 'JavaScript' : 'Python')}
        </span>

        {executionContext === 'nodejs' && (
          <div className="nb-lang-toggle">
          <button
              className={`nb-btn--lang ${displayLang === 'python' ? 'active' : ''}`}
              onClick={() => {
                displayLangRef.current = 'python';
                setDisplayLang('python');
                const model = editorRef.current?.getModel();
                if (model) model.setValue(codeRef.current);
              }}
              title="Show Python code"
            >
              🐍 Python
            </button>
            <button
              className={`nb-btn--lang ${displayLang === 'javascript' ? 'active' : ''}`}
              onClick={() => {
                displayLangRef.current = 'javascript';
                setDisplayLang('javascript');
                const model = editorRef.current?.getModel();
                if (model) model.setValue(jsCode || '// JavaScript not yet generated');
              }}
              title="Show JavaScript code"
            >
              📜 JS
            </button>
          </div>
        )}

        <div className="nb-toolbar__actions">

          {/* File upload — only for data source cells */}
          {FILE_UPLOAD_COMPONENT_IDS.includes(componentId) && (
            <>
              {/* Test data dropdown */}
              <div className="nb-test-data-wrapper">
                <button onClick={handleTestDataClick} className="nb-btn--test-data">
                  {selectedTestFile ? `📄 ${selectedTestFile} ▾` : '💾 Test Data ▾'}
                </button>
                {showTestMenu && testFiles.length > 0 && (
                  <div className="nb-test-menu">
                    {testFiles.map(f => (
                      <div
                        key={f}
                        className="nb-test-menu__item"
                        onClick={() =>
                          IMAGE_COMPONENT_IDS.includes(componentId)
                            ? handleTestImageSelect(f)
                            : handleTestFileSelect(f)
                        }
                      >
                        📄 {f}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload CSV/Image */}
              <label className="nb-btn--upload">
                {IMAGE_COMPONENT_IDS.includes(componentId) ? '🖼️ Upload Image' : '📂 Upload CSV'}
                <input
                  type="file"
                  accept={IMAGE_COMPONENT_IDS.includes(componentId) ? 'image/*' : '.csv'}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </label>
            </>
          )}

          {/* Regenerate */}
          <button
            // onClick={() => onRegenerateCell?.(id)}
            onClick={() => confirmAction(
              'Regenerate this cell\'s code from the pipeline configuration?',
              () => onRegenerateCell?.(id),
              { title: 'Regenerate Cell', icon: '🔄' }
            )}
            title="Regenerate cell code from pipeline configuration"
            className="nb-btn--icon nb-btn--regenerate"
            disabled={isRegenerating}
          >
            {isRegenerating ? '⏳ Regenerating' : '🔄 1. Regenerate'}
          </button>

          {/* Translate — AgenticAI only */}
          {executionContext === 'nodejs' && (
            <button
              // onClick={handleTranslate}
              onClick={() => confirmAction(
                'Translate this Python code to JavaScript?',
                handleTranslate,
                { title: 'Translate to JavaScript', icon: '🔄' }
              )}
              disabled={isTranslating}
              title="Translate Python to JavaScript"
              className="nb-btn--icon nb-btn--translate"
            >
              {isTranslating ? '⏳ Translating' : '🔄 2. Translate'}
            </button>
          )}

          {/* Run cell */}
          <button
            // onClick={handleRun}
            onClick={() => confirmAction('Run this cell?', handleRun, { title: 'Run Cell', icon: '▶️' })}
            disabled={isRunning || pyLoading}
            title="Run cell (Shift+Enter)"
            className="nb-btn--run"
          >
            <Play size={12} />
            {isRunning ? 'Running...' : '3. Run'}
          </button>

          {/* Save */}
          <button onClick={handleSave} title="Save cell code" className="nb-btn--icon nb-btn--save">
            💾
          </button>

          {/* Insert cell below */}
          <button
            onClick={() => onInsertBelow(id)}
            title="Insert cell below"
            className="nb-btn--icon nb-btn--insert"
          >
            <Plus size={12} />
          </button>

          {/* Delete cell */}
          <button
            onClick={() => onDelete(id)}
            title="Delete cell"
            className="nb-btn--icon nb-btn--delete"
          >
            <Trash2 size={12} />
          </button>

          {isRegenerating && (
            <div className="ai-loading-overlay">
              <div className="ai-loading-spinner" />
              <p className="ai-loading-title">
                🤖 Prof-iX is re-generating your code...
              </p>
              {/* <p className="ai-loading-subtitle"> */}
              <p className="ai-loading-subtitle">🤖 Building cell: {label || componentId || id}</p>
              {/* </p> */}
            </div>
          )}

        </div>
      </div>

      {/* ── Full Monaco Editor ─────────────────────────────────────────────────── */}
      {!isCollapsed && (
        <div className="nb-editor" ref={editorContainerRef} onKeyDown={handleKeyDown}>
          <Editor
            defaultLanguage="python"
            language={displayLang === 'javascript' ? 'javascript' : 'python'}
            defaultValue={initialCode}
            theme="vs-dark"
            onMount={handleEditorMount}
            onChange={(value) => {
              if (displayLangRef.current === 'javascript') {
                setJsCode(value || '');
                onJsCodeChange?.(id, value || '');
              } else {
                codeRef.current = value || '';
                onCodeChange(id, value || '');
              }
            }}
            options={{
              minimap:              { enabled: false },
              scrollBeyondLastLine: false,
              fontSize:             13,
              lineHeight:           20,
              padding:              { top: 10, bottom: 10 },
              lineNumbers:          'on',
              folding:              false,
              renderLineHighlight:  'line',
              scrollbar:            { vertical: 'hidden', horizontal: 'hidden' },
              overviewRulerLanes:   0,
              automaticLayout:      false,
              tabSize:              4,
              wordWrap:             'on',
            }}
          />
        </div>
      )}

      {/* ── One-line Monaco Editor ─────────────────────────────────────────────────── */}
      {isCollapsed && (
        <div className="nb-cell__collapsed-preview">
          {(codeRef.current || '').split('\n').find(l => l.trim()) || '(empty)'}
        </div>
      )}

      {(isTranslating || isRunning) && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-spinner" />
          <p className="ai-loading-title">
            🤖 Prof-iX is {isTranslating ? 'Translating to JavaScript…' : 'Running…'}
          </p>
          <p className="ai-loading-subtitle">🤖 Translating cell: {label || componentId || id}</p>
        </div>
      )}

      {/* ── Running indicator ─────────────────────────────────────────────── */}
      {isRunning && (
        <div className="nb-running">
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__label">Executing…</span>
        </div>
      )}

      {/* ── Output area ───────────────────────────────────────────────────── */}
      {(hasOutput || (isRunning === false && output !== null)) && (
        <div className="nb-output">

          {/* Expected output label pills */}
          {simOutputs.length > 0 && (
            <div className="nb-output__labels">
              {simOutputs.map((out, i) => (
                <span key={i} className="nb-output__label-pill">
                  {out.name}
                  {out.unit    ? ` (${out.unit})`    : ''}
                  {out.dataType ? ` · ${out.dataType}` : ''}
                </span>
              ))}
            </div>
          )}

          {/* Actual output */}
          <div className={`nb-output__content ${output?.error ? 'nb-output__content--error' : ''}`}>

            {/* {output?.stdout && (
              <div className="nb-output__stdout">
                {output.stdout.split('\n').map(renderStdoutLine)}
              </div>
            )} */}

            {output?.stdout && (
              isGraphRunnerOutput(output.stdout)
                ? <GraphRunnerOutput stdout={output.stdout} />
                : (
                  <div className="nb-output__stdout">
                    {output.stdout.split('\n').map(renderStdoutLine)}
                  </div>
                )
            )}

            {output?.error && (
              <pre className="nb-output__error">{output.error}</pre>
            )}

            {output?.plot && (
              <img
                src={`data:image/png;base64,${output.plot}`}
                alt="Plot output"
                className={`nb-output__plot ${output?.stdout ? 'nb-output__plot--with-stdout' : ''}`}
              />
            )}

            {!output?.error && !output?.stdout && !output?.plot && output !== null && (
              <span className="nb-output__empty">(no output)</span>
            )}

          </div>
        </div>
      )}

      <CustomAlert
        visible={alertVisible}
        title={alertConfig.title}
        icon={alertConfig.icon}
        message={alertConfig.message}
        onConfirm={alertConfig.onConfirm}
        onCancel={() => setAlertVisible(false)}
      />

    </div>
  );
});

export default NotebookCell;
